import $ from 'jquery';
import { delay } from '../utilities/util';

const CONFIG = {
    DEFAULT_TRANSITION_LENGTH: 250,
};

const SELECTORS = {
    LINK: '.js-tabView-link',
    CONTENT: '.js-tabView-content',
    CURRENT: 'current mix-iconBackground_chevronAlt',
};

/**
 * @fileOverview View handles updating the visual elements on the page
 * Controls the display of content depending on which tab is selected
 */
export default class TabView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * Track the ID of the current tab
         * @type {string}
         */
        this.currentTab = '';

        /**
         * Track if the page is being rendered for the first time
         * @type {boolean}
         */
        this.isFirstRender = true;

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        /**
         * If element has no links, exit early
         */
        this.$link = this.$element.find(SELECTORS.LINK);
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {TabView}
     */
    setupHandlers() {
        this.handleClick = this.onClick.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {TabView}
     */
    createChildren() {
        this.$content = this.$element.find(SELECTORS.CONTENT);
        this.$contentChildren = this.$content.children();
        this.currentTab = this.getHash();

        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {TabView}
     */
    layout() {
        this.updateLinks();
        this.updateContent();
        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {TabView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$link.length; i++) {
            this.$link[i].addEventListener('click', this.handleClick);
        }

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {TabView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$link.length; i++) {
            this.$link[i].removeEventListener('click', this.handleClick);
        }

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {TabView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * check page for hash and return it
     * otherwise return first hash so the page can be defaulted to the first linked content
     *
     * @returns {string}
     */
    getHash() {
        let hash = this.$link[0].hash;
        this.$link.each((key) => {
            if (window.location.hash === this.$link[key].hash) {
                hash = window.location.hash;
                return false; // Exit loop early
            }
        });

        return hash;
    }

    /**
     * Handles click event
     *
     * @param event
     * @returns {TabView}
     */
    onClick(event) {
        const $target = $(event.currentTarget);
        const targetId = $target.prop('hash');

        if (this.$content.find(targetId)) {
            this.currentTab = targetId;
        }

        this.render();

        return this;
    }

    /**
     * Update page links and set current tab
     *
     * @returns {TabView}
     */
    updateLinks() {
        this.$link.parent().removeClass(SELECTORS.CURRENT);
        this.$link.each((key) => {
            if (this.$link[key].hash === this.currentTab) {
                $(this.$link[key]).parent().addClass(SELECTORS.CURRENT);
                return false; // Exit loop early
            }
        });

        return this;
    }

    /**
     * Upadate tab content to show current content and hide others
     * Manage aria attributes for screen readers
     *
     * @returns {Promise.<TabView>}
     */
    async updateContent() {
        let transition = CONFIG.DEFAULT_TRANSITION_LENGTH / 2;

        if (this.isFirstRender) {
            transition = 0;
            this.isFirstRender = false;
        }

        this.$contentChildren.each((key) => {
            $(this.$contentChildren[key]).fadeOut(transition)
                                         .attr('aria-hidden', 'true')
                                         .removeAttr('aria-live');
        });

        await delay(transition);
        this.$content.find(this.currentTab).fadeIn(transition)
                                           .attr('aria-hidden', 'false')
                                           .attr('aria-live', 'polite');

        return this;
    }
}
